import React, { useState } from 'react';
import { trackContactSubmit, trackFreeTrial } from '../utils/tracking';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.knine.ai';

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company: form.company || '',
          phone: form.phone || '',
          message: form.message,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      trackContactSubmit();
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="container hero-content fade-up">
          <div className="section-badge blue">Contact</div>
          <h1 className="hero-title" style={{ fontSize: '2.75rem' }}>
            Get in <span className="text-gradient">Touch</span>
          </h1>
          <p className="hero-subtitle">
            Have a question, need a demo, or want to talk about enterprise pricing?
            Our team is ready to help.
          </p>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', maxWidth: '960px', margin: '0 auto' }}>

            {/* Contact Form */}
            <div className="card" style={{ padding: '40px' }}>
              {!submitted ? (
                <>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '24px' }}>
                    Send Us a Message
                  </h3>
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="john@company.com"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Acme Recruiting"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Message</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        className="form-textarea"
                        placeholder="Tell us how we can help..."
                        maxLength={5000}
                        required
                      />
                    </div>

                    {error && (
                      <div style={{
                        padding: '12px 16px',
                        marginBottom: '16px',
                        borderRadius: '8px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        fontSize: '0.9rem',
                      }}>
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-gradient btn-lg"
                      style={{ width: '100%' }}
                      disabled={loading}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🐕</div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px' }}>
                    Message Sent!
                  </h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7 }}>
                    Thanks for reaching out! We've received your request and will be in contact with you shortly.
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, marginTop: '12px' }}>
                    In the meantime, feel free to contact us directly at{' '}
                    <a href="tel:4404854497" style={{ fontWeight: 600 }}>(440) 485-4497</a>{' '}
                    or email{' '}
                    <a href="mailto:info@knine.ai" style={{ fontWeight: 600 }}>info@knine.ai</a>.
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, marginTop: '16px' }}>
                    Or if you'd like to take kNine for a walk,{' '}
                    <a href="https://app.knine.ai/register" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                      click here to get started today
                    </a>.
                  </p>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '24px' }}>
                Other Ways to Reach Us
              </h3>

              <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div className="card-icon blue" style={{ marginBottom: 0, flexShrink: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)', marginBottom: '4px' }}>Email</div>
                    <a href="mailto:info@knine.ai" style={{ fontSize: '0.9rem' }}>info@knine.ai</a>
                  </div>
                </div>
              </div>

              <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div className="card-icon purple" style={{ marginBottom: 0, flexShrink: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)', marginBottom: '4px' }}>Phone</div>
                    <a href="tel:4404854497" style={{ fontSize: '0.9rem' }}>(440) 485-4497</a>
                  </div>
                </div>
              </div>

              <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div className="card-icon green" style={{ marginBottom: 0, flexShrink: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)', marginBottom: '4px' }}>Response Time</div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>We typically respond within 24 hours during business days.</div>
                  </div>
                </div>
              </div>

              <div className="card-flat" style={{ padding: '24px', background: 'var(--primary-bg)', border: '1px solid #dbeafe' }}>
                <h4 style={{ fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '8px', fontSize: '1rem' }}>
                  Ready to Get Started?
                </h4>
                <p style={{ fontSize: '0.9rem', color: 'var(--primary)', lineHeight: 1.6, marginBottom: '16px' }}>
                  Skip the form and dive right in with our free trial. No credit card required.
                </p>
                <a href="https://app.knine.ai/register" className="btn btn-primary btn-sm" target="_blank" rel="noopener noreferrer" onClick={() => trackFreeTrial('contact_sidebar')}>Start Free Trial</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
